import { defineMessages } from "@/lib/i18n/messages";

export default defineMessages({
  es: {
    title: "Ranking · La palabra del día",
    heading: "Ranking",
    description: "Los mejores jugadores de La palabra del día esta semana y este mes, en el mundo y en tu país.",
    subtitle: "Compite por puntos o por tiempo, en el mundo o en tu país.",
    pointsHint: "Ganar en 1 intento suma 6 puntos; en 6 intentos, 1; sin resolverla, 0. Si hay empate, gana quien usó menos intentos.",
    byPoints: "Por puntos",
    byTime: "Por tiempo",
    average: "Tiempo promedio",
    best: "Mejor tiempo",
    allTime: "Histórico",
    averageHint:
      "El promedio de todos tus tiempos, contando solo las partidas que terminaste. Si hay empate, gana quien terminó más.",
    bestHint: "Tu partida más rápida de siempre. Si hay empate, gana quien llegó primero a esa marca.",
    dailyWordTimeHint: "El reloj parte con la primera letra que escribes y para al acertar.",
    week: "Esta semana",
    month: "Este mes",
    global: "Global",
    columns: {
      player: "Jugador",
      points: "Pts",
      wins: "Victorias",
      streak: "Racha",
      average: "Promedio",
      best: "Mejor",
      completed: "Partidas",
    },
    emptyTime: "Todavía nadie del ranking terminó una partida en este periodo. ¡Puedes ser el primero!",
    wordSearch: {
      title: "Ranking · Sopa de letras",
      description: "Los jugadores más rápidos de la Sopa de letras en cada nivel, en el mundo y en tu país.",
      subtitle: "Un ranking por nivel: quién encuentra todas las palabras más rápido.",
      play: "Jugar la sopa de hoy",
    },
    you: "Tú",
    onlyYouSee: "Solo tú ves esta fila: súmate para aparecer en el ranking.",
    topPercent: (pct: number, where: string) => `Estás en el ${pct}% superior ${where}.`,
    whereGlobal: "del mundo",
    whereCountry: (country: string) => `de ${country}`,
    empty: "Todavía nadie se sumó al ranking en este periodo. ¡Puedes ser el primero!",
    loadError: "No pudimos cargar el ranking.",
    retry: "Reintentar",
    backToGame: "Volver al juego",
    play: "Jugar la palabra de hoy",
    loginCta: "Inicia sesión para competir y ver tu puesto.",
    login: "Iniciar sesión",
    editProfile: "Cómo aparezco",
    viewRanking: "Ver ranking",
    join: {
      title: "¿Quieres aparecer en el ranking?",
      body: "Así te verían los demás jugadores. Puedes cambiarlo o salirte cuando quieras.",
      yes: "Sumarme",
      edit: "Editar",
      no: "Ahora no",
      joined: "¡Listo! Ya apareces en el ranking.",
    },
    profile: {
      title: "Cómo apareces en el ranking",
      description: "Nunca mostramos tu correo ni tus apellidos.",
      joined: "Aparecer en el ranking",
      joinedHelp: "Si lo desactivas, solo tú ves tu puesto.",
      alias: "Alias",
      aliasHelp: (name: string) => `Opcional. Si lo dejas vacío, mostramos tu primer nombre: ${name}.`,
      showPhoto: "Mostrar mi foto de perfil",
      noPhoto: "No tienes foto de perfil: mostramos tu avatar de juego.",
      photoHidden: "Con la foto oculta, mostramos tu avatar de juego.",
      preview: "Vista previa",
      save: "Guardar",
      saving: "Guardando…",
      cancel: "Cancelar",
      saved: "Guardamos tus cambios.",
      error: "No pudimos guardar los cambios.",
    },
  },
  en: {
    title: "Leaderboard · Word of the day",
    heading: "Leaderboard",
    description: "The top Word of the day players this week and this month, worldwide and in your country.",
    subtitle: "Compete on points or on time, worldwide or in your country.",
    pointsHint: "Winning in 1 try earns 6 points; in 6 tries, 1; not solving it, 0. Ties go to whoever used fewer tries.",
    byPoints: "By points",
    byTime: "By time",
    average: "Average time",
    best: "Best time",
    allTime: "All time",
    averageHint: "The average of all your times, counting only the games you finished. Ties go to whoever finished more.",
    bestHint: "Your fastest game ever. Ties go to whoever reached that time first.",
    dailyWordTimeHint: "The clock starts with the first letter you type and stops when you solve it.",
    week: "This week",
    month: "This month",
    global: "Global",
    columns: {
      player: "Player",
      points: "Pts",
      wins: "Wins",
      streak: "Streak",
      average: "Average",
      best: "Best",
      completed: "Games",
    },
    emptyTime: "Nobody on the leaderboard has finished a game in this period yet. You could be the first!",
    wordSearch: {
      title: "Leaderboard · Word search",
      description: "The fastest Word search players on each level, worldwide and in your country.",
      subtitle: "One leaderboard per level: who finds all the words fastest.",
      play: "Play today's word search",
    },
    you: "You",
    onlyYouSee: "Only you can see this row: join to appear on the leaderboard.",
    topPercent: (pct: number, where: string) => `You're in the top ${pct}% ${where}.`,
    whereGlobal: "worldwide",
    whereCountry: (country: string) => `in ${country}`,
    empty: "Nobody has joined the leaderboard for this period yet. You could be the first!",
    loadError: "We couldn't load the leaderboard.",
    retry: "Try again",
    backToGame: "Back to the game",
    play: "Play today's word",
    loginCta: "Log in to compete and see your rank.",
    login: "Log in",
    editProfile: "How I appear",
    viewRanking: "See leaderboard",
    join: {
      title: "Want to appear on the leaderboard?",
      body: "This is how other players would see you. You can change it or leave anytime.",
      yes: "Join",
      edit: "Edit",
      no: "Not now",
      joined: "Done! You're on the leaderboard.",
    },
    profile: {
      title: "How you appear on the leaderboard",
      description: "We never show your email or last name.",
      joined: "Appear on the leaderboard",
      joinedHelp: "If you turn this off, only you can see your rank.",
      alias: "Alias",
      aliasHelp: (name: string) => `Optional. If you leave it empty, we show your first name: ${name}.`,
      showPhoto: "Show my profile photo",
      noPhoto: "You don't have a profile photo: we show your game avatar.",
      photoHidden: "With your photo hidden, we show your game avatar.",
      preview: "Preview",
      save: "Save",
      saving: "Saving…",
      cancel: "Cancel",
      saved: "Your changes were saved.",
      error: "We couldn't save your changes.",
    },
  },
});
